import { CommonUtility } from '@utility/common';
import {
  AppTable,
  BorderTableRow,
  BorderTableCell,
  AppTableCell,
} from '@components/AppTable';
import { useMemo, type ReactNode } from 'react';
import { cn } from '@skupreme/shared/ui';
import { DateFormat, DateUtility } from '@utility/date';
import { Link, useNavigate } from 'react-router';
import {
  CommonConstant,
  POStatus,
  POStatusNames,
  POStatusReadable,
} from '@utility/constants/common';
import {
  BodyContainer,
  BoldText,
  ColorText,
  FlexRow,
  MuteText,
} from '@elements/Common';
import {
  ItemComponentsRow,
  ItemConflictTitle,
  ItemImage as CatalogItemImage,
} from '@components/CatalogElements';
import { FilterPill } from '@components/Filter';
import {
  POStatusBox,
  PoStatusFunction,
  ProgressBar,
  calculateAdjustments,
} from '@page-components/purchase-order';
import { useMetadata } from '@contexts/org-metadata';
import { OrganizationCell } from '@page-components/scm/common';
import { useAuth } from '@contexts/auth';
import TextCopyToClipboard from '@components/TextCopyToClipboard';
import useUrlPermission from '@hooks/url-permission';
import { Flag } from '@elements/Flag';
import { Border } from '@page-components/scm/style';

type SegmentTone = 'done' | 'current' | 'pending';

const SEGMENT_TONE: Record<SegmentTone, string> = {
  done: 'sk:text-success',
  current: 'sk:text-warning',
  pending: 'sk:text-fill-secondary',
};

type Segment = { className: string; fill: string };

/*
 * Callers may still hand in a colour of their own for the current step; the
 * rest of the ring is drawn in kit tones through `currentColor`.
 */
function trackerSegments(count: number, index: number, color?: string) {
  return Array.from({ length: count }, (_, i): Segment => {
    if (i === index && color) {
      return { className: '', fill: color };
    }
    const tone: SegmentTone =
      i === index ? 'current' : i < index ? 'done' : 'pending';
    return { className: SEGMENT_TONE[tone], fill: 'currentColor' };
  });
}

function TrackerCount({ children }: { children: ReactNode }) {
  return (
    <div className="sk:absolute sk:top-[4.5px] sk:left-[4.5px] sk:flex sk:size-[22.5px] sk:items-center sk:justify-center sk:rounded-full sk:bg-surface sk:text-[10px] sk:text-fg-tertiary">
      {children}
    </div>
  );
}

type TrackerProps = { index?: number; color?: string };

export function StatusTracker({ index = 2, color }: TrackerProps) {
  const segments = useMemo(
    () => trackerSegments(4, index, color),
    [index, color],
  );

  return (
    <div className="sk:relative sk:size-8 sk:shrink-0">
      <TopRight {...segments[0]} />
      <BottomRight {...segments[1]} />
      <BottomLeft {...segments[2]} />
      <TopLeft {...segments[3]} />
      <TrackerCount>{index + 1}/4</TrackerCount>
    </div>
  );
}

export function StatusTrackerTrio({ index = 2, color }: TrackerProps) {
  const segments = useMemo(
    () => trackerSegments(3, index, color),
    [index, color],
  );

  return (
    <div className="sk:relative sk:size-8 sk:shrink-0 sk:overflow-hidden sk:rounded-full">
      <CirclePart1of3 {...segments[0]} />
      <CirclePart2of3 {...segments[1]} />
      <CirclePart3of3 {...segments[2]} />
      <TrackerCount>{index + 1}/3</TrackerCount>
    </div>
  );
}

function CirclePart1of3({ className, fill }: Segment) {
  return (
    <svg
      aria-hidden
      className={cn('sk:absolute sk:-top-1.5 sk:left-4.5', className)}
      width="22"
      height="32"
      viewBox="0 0 22 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0 21.3333V0C12.1503 0 22 9.55126 22 21.3333C22 25.0781 20.9835 28.7569 19.0526 32L0 21.3333Z"
        fill={fill}
      />
    </svg>
  );
}

function CirclePart2of3({ className, fill }: Segment) {
  return (
    <svg
      aria-hidden
      className={cn('sk:absolute sk:top-4.5 sk:-left-0.75', className)}
      width="37"
      height="22"
      viewBox="0 0 37 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.5 0L37 10.9982C31.1011 21.5188 18.0363 25.1235 7.81902 19.0494C4.57159 17.1188 1.8749 14.342 0 10.9982L18.5 0Z"
        fill={fill}
      />
    </svg>
  );
}

function CirclePart3of3({ className, fill }: Segment) {
  return (
    <svg
      aria-hidden
      className={cn('sk:absolute sk:-top-1.5 sk:-left-2.25', className)}
      width="22"
      height="32"
      viewBox="0 0 22 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22 21.3333L2.95062 32C-3.1235 21.7964 0.481155 8.74916 11.0018 2.85812C14.3457 0.985734 18.1388 0 22 0V21.3333Z"
        fill={fill}
      />
    </svg>
  );
}

export const publicStatusReadable = {
  open: 'Draft',
  pending: 'Awaiting Supplier Action',
  accepted: 'Awaiting Payment',
  rejected: 'Revise',
  pending_payment: 'Confirm Payment',
  pending_final_payment: 'Awaiting Payment',
  completed: 'Complete',
  archived: 'Deleted',
  pending_action: 'Pending Supplier Action',
};

function POStatusText({ status, publicStatus }) {
  const PoStatusObj = PoStatusFunction(status, publicStatus);
  const isOpenStatus =
    status === POStatus.open && publicStatus === POStatus.pending;
  return (
    // @ts-expect-error - TS2741: Property 'isPublic' is missing in type '{ status: any; title: string; }' but req...
    <POStatusBox
      status={status}
      title={isOpenStatus ? POStatusReadable.open : PoStatusObj.title}
    />
  );
}

export function StatusRenderer({
  noTitle,
  enableTracker = true,
  back = false,
  status,
  publicStatus,
}) {
  const PoStatusObj = PoStatusFunction(status, publicStatus);
  return (
    <div className="sk:flex! sk:items-center!">
      {enableTracker &&
        !back &&
        PoStatusObj.title !== POStatusNames.archived && (
          <StatusTracker index={PoStatusObj.indicator} />
        )}
      {!noTitle && (
        <ColorText className="sk:mx-2!" color={PoStatusObj.color}>
          <BoldText>{PoStatusObj.title}</BoldText>
        </ColorText>
      )}
      {enableTracker &&
        back &&
        PoStatusObj.title !== POStatusNames.archived && (
          <StatusTracker index={PoStatusObj.indicator} />
        )}
    </div>
  );
}

function BottomRight({ className, fill }: Segment) {
  return (
    <svg
      aria-hidden
      className={cn('sk:absolute sk:top-4.5 sk:left-4.5', className)}
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.5 0H13.9259C13.2223 7.08663 7.58663 12.7223 0.5 13.4259V0Z"
        fill={fill}
      />
    </svg>
  );
}
function TopRight({ className, fill }: Segment) {
  return (
    <svg
      aria-hidden
      className={cn('sk:absolute sk:top-0 sk:left-4.5', className)}
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.9259 14.0002H0.5V0.574219C7.58663 1.27782 13.2223 6.91353 13.9259 14.0002Z"
        fill={fill}
      />
    </svg>
  );
}
function TopLeft({ className, fill }: Segment) {
  return (
    <svg
      aria-hidden
      className={cn('sk:absolute sk:top-0 sk:left-0', className)}
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0.0742188 14.0002C0.777822 6.91353 6.41353 1.27782 13.5002 0.574219V14.0002H0.0742188Z"
        fill={fill}
      />
    </svg>
  );
}
function BottomLeft({ className, fill }: Segment) {
  return (
    <svg
      aria-hidden
      className={cn('sk:absolute sk:top-4.5 sk:left-0', className)}
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.5002 0V13.4259C6.41353 12.7223 0.777822 7.08663 0.0742188 0H13.5002Z"
        fill={fill}
      />
    </svg>
  );
}

const CELL_ROW = 'sk:flex sk:flex-col sk:text-left';

const PO_LINK = 'sk:text-link sk:hover:text-link sk:hover:underline';

const NoBreakMuteText = 'sk:inline-block sk:w-6.25 sk:shrink-0';

function Row({ item, rowClicked, showUnAllocatedView }) {
  /* @ts-expect-error - TS2339: Property 'isSummaryView' does not exist on type 'unknown'. */
  const { isSummaryView } = useMetadata();
  const { switchOrganizationIfDiff } = useAuth();

  const PoStatusObj = PoStatusFunction(item.status, item.public_status);

  const { images, total } = useMemo(() => {
    let tempImage = (item.items || []).map(
      tempItem =>
        tempItem.p_images?.display_image || CommonConstant.defaultImage,
    );

    tempImage = [
      ...tempImage,
      ...(item.components || []).map(component =>
        component.images ? component.images[0] : CommonConstant.defaultImage,
      ),
    ];

    const temp = calculateAdjustments(item.price, item.adjustments);

    return {
      images: tempImage,
      total: temp,
    };
  }, [item]);

  const { disabled, hidden } = useUrlPermission();

  const handleRedirect = async () => {
    if (disabled || hidden) return;

    if (isSummaryView) {
      const organizationId = item?.organization?._id || '';
      if (organizationId) {
        await switchOrganizationIfDiff(organizationId);
      }
    }
  };

  const getEstimationDays = estimatedDate => {
    const isCompleted = PoStatusObj.title === POStatusNames.completed;
    const days = isCompleted
      ? DateUtility.difference(new Date(estimatedDate), new Date())
      : DateUtility.difference(new Date(), new Date(estimatedDate));

    return isCompleted
      ? `(${Math.abs(days)} Days Ago)`
      : `(in ${Math.abs(days)} Days)`;
  };

  const getPoStatusDetails = (status, publicStatus) => {
    if (
      (status === POStatus.pendingPayment &&
        publicStatus === POStatus.pending) ||
      (status === POStatus.open && publicStatus === POStatus.pending) ||
      (status === POStatus.pendingFinalPayment &&
        publicStatus === POStatus.pending)
    ) {
      return 'Awaiting Supplier Review';
    }
    const text = publicStatus || status;
    if (
      text === POStatus.accept &&
      (status === POStatus.pendingFinalPayment ||
        status === POStatus.pendingPayment)
    ) {
      return 'Submit Payment Documents';
    }
    return '';
  };

  function ShipmentAllocationCell({ allocation_percentage, unallocation_qty }) {
    return (
      <>
        <div>
          <span
            className={cn(
              'sk:font-bold',
              allocation_percentage === 100
                ? 'sk:text-success'
                : 'sk:text-danger',
            )}
          >
            {allocation_percentage === 100 ? (
              'All Items Allocated'
            ) : (
              <>
                {CommonUtility.numberWithCommas(unallocation_qty)} Items to{' '}
                <br /> Allocate (
                {CommonUtility.roundNumber(allocation_percentage)}%)
              </>
            )}
          </span>
        </div>
        <ProgressBar progress={allocation_percentage} />
      </>
    );
  }

  function TimelineSection({
    dateIssused,
    estimatedCompletion,
    displayId,
    id,
    fullWidth,
  }: {
    dateIssused: string;
    estimatedCompletion: string;
    displayId?: string;
    id?: string;
    fullWidth?: boolean;
  }) {
    return (
      <div
        className={cn(
          CELL_ROW,
          'sk:mb-2 sk:text-xs',
          fullWidth
            ? 'sk:w-full sk:min-w-55'
            : 'sk:w-42.5 sk:max-[1330px]:w-37.5',
        )}
      >
        {displayId && (
          <Link
            to={`/app/scm/purchase-orders/${id}`}
            className={cn('sk:flex sk:justify-between', PO_LINK)}
          >
            <MuteText>Display ID &nbsp; </MuteText>
            {displayId}
          </Link>
        )}
        <span className="sk:flex sk:justify-between">
          <MuteText>Issued &nbsp; </MuteText>
          {DateUtility.formatDate(dateIssused, DateFormat.dateWithShortYear)}
        </span>
        <span className="sk:flex sk:justify-between">
          <MuteText>
            {PoStatusObj.title === POStatusNames.completed
              ? POStatusNames.completed
              : 'Est. Completion'}
            &nbsp;{' '}
          </MuteText>
          {DateUtility.formatDate(
            estimatedCompletion,
            DateFormat.dateWithShortYear,
          )}
        </span>
        <MuteText>{getEstimationDays(estimatedCompletion)}</MuteText>
      </div>
    );
  }

  if (showUnAllocatedView) {
    return (
      <BorderTableRow className="catalog-row">
        <BorderTableCell>
          <Link
            to={`/app/catalogs/catalog/${encodeURIComponent(item._id)}`}
            className="sk:relative sk:block"
          >
            <CatalogItemImage item={item} size="mini" />
          </Link>
        </BorderTableCell>

        <AppTableCell singleLine={false}>
          <div className="sk:pr-3.5!">
            <ItemConflictTitle
              item={item}
              id={item._id}
              sku={item.sku}
              asin={item.secondary_identifier}
              title={item.short_title || item.p_title}
              upc={item.upc}
            />
          </div>
        </AppTableCell>
        <AppTableCell>
          {item.purchase_orders?.map((purchaseOrder, i) => (
            <div key={purchaseOrder.purchase_order_id} className="sk:pr-3">
              <TimelineSection
                dateIssused={purchaseOrder?.date_issued}
                estimatedCompletion={purchaseOrder?.estimated_completion}
                displayId={purchaseOrder.display_id}
                fullWidth
                id={purchaseOrder.purchase_order_id}
              />
              {i + 1 !== item.purchase_orders?.length && (
                <Border className="sk:my-2" />
              )}
            </div>
          ))}
        </AppTableCell>
        <AppTableCell data-field-value="PO-shipment-allocation">
          {item.purchase_orders?.map(purchaseOrder => (
            <div key={purchaseOrder.purchase_order_id} className="sk:py-2">
              <ShipmentAllocationCell
                allocation_percentage={purchaseOrder.allocation_percentage}
                unallocation_qty={
                  (purchaseOrder.qty || 0) - (purchaseOrder.shipped_qty || 0)
                }
              />
            </div>
          ))}
        </AppTableCell>
      </BorderTableRow>
    );
  }

  return (
    <BorderTableRow
      onClick={async () => {
        await handleRedirect();
        rowClicked(item);
      }}
      className="action-hover sk:cursor-pointer"
      textAlign="center"
      verticalAlign="top"
    >
      <BorderTableCell data-field-value="PO-products">
        <div className="border" />
        <div className="sk:max-w-full sk:overflow-x-auto sk:[&_.img-container]:w-[33%] sk:[&_.img-container]:max-w-20">
          <ItemComponentsRow images={images} />
        </div>
      </BorderTableCell>
      <AppTableCell data-field-value="PO-supplier">
        <div className={cn(CELL_ROW, 'sk:mt-2')}>
          <FlexRow>
            {item.supplier?.country && <Flag name={item.supplier?.country} />}
            <BoldText className="sk:text-xs">
              {item.supplier?.supplier_name}
            </BoldText>
          </FlexRow>
          {item.manufacturer_reference_number && (
            <div className="sk:flex">
              <MuteText className={cn(NoBreakMuteText, 'sk:text-xs')}>
                PI
              </MuteText>
              <span className="sk:text-xs">
                {item.manufacturer_reference_number}
              </span>
            </div>
          )}
          <div className="sk:flex sk:items-center">
            <MuteText className={cn(NoBreakMuteText, 'sk:text-xs')}>
              P0
            </MuteText>
            <Link
              to={`/app/scm/purchase-orders/${item._id}`}
              className={cn('sk:text-xs', PO_LINK)}
              onClick={e => e.stopPropagation()}
            >
              {item.display_id}
            </Link>
            <TextCopyToClipboard showText={false} text={item.display_id} />
          </div>
          {isSummaryView && <OrganizationCell data={item?.organization} />}
        </div>
      </AppTableCell>
      <AppTableCell data-field-value="PO-total">
        <div className="sk:pt-2!">
          {CommonUtility.currencyFormat(
            item.price + total.adjustPricesTotal,
            item.currency,
          )}
        </div>
      </AppTableCell>
      <AppTableCell data-field-value="PO-status" className="action-cell">
        <TimelineSection
          dateIssused={item.date_issued}
          estimatedCompletion={item.estimated_completion}
        />
      </AppTableCell>
      <AppTableCell data-field-value="PO-shipment-allocation">
        <ShipmentAllocationCell
          allocation_percentage={item.allocation_percentage}
          unallocation_qty={item.unallocation_qty}
        />
      </AppTableCell>
      <AppTableCell data-field-value="PO-action">
        <POStatusText status={item.status} publicStatus={item.public_status} />
        {getPoStatusDetails(item.status, item.public_status) && (
          <div className="sk:mt-2.5 sk:text-left sk:text-xs">
            {getPoStatusDetails(item.status, item.public_status)}
          </div>
        )}
      </AppTableCell>
    </BorderTableRow>
  );
}

export function PurchaseOrderList({
  loading,
  data,
  currentPage,
  totalPages,
  pageChanged,
  pageSize,
  sizeChanged,
  totalData,
  sort,
  orderChanged,
  filters,
  removeFilter,
  userValue,
  openRelevantFilter,
  showUnAllocatedView,
}) {
  // @ts-expect-error - TS2339: Property 'isSummaryView' does not exist on type 'unknown'.
  const { isSummaryView } = useMetadata();

  const navigate = useNavigate();

  const columns = useMemo(() => {
    if (showUnAllocatedView) {
      return [
        {
          text: 'Products',
          width: 1,
          key: 'info',
          textAlign: 'left',
        },
        {
          text: '',
          width: 3,
          key: 'details',
        },
        {
          text: 'Purchase Orders',
          width: 3,
          key: 'purchase_orders',
          textAlign: 'left',
        },
        {
          text: 'Shipment Allocation',
          width: 3,
          textAlign: 'left',
          key: 'shipment_allocation',
        },
      ];
    }
    const arr = [
      {
        text: 'Products',
        width: 3,
        key: 'info',
        textAlign: 'left',
      },
      {
        text: isSummaryView ? 'Supplier / Brand' : 'Supplier',
        key: 'Supplier',
        width: 3,
        textAlign: 'left',
      },
      {
        text: 'Total',
        key: 'Price',
        width: 2,
        textAlign: 'center',
      },
      {
        text: 'Timeline',
        key: 'status',
        width: 3,
        sortKey: 'sort_issued_date',
        textAlign: 'left',
      },
      {
        text: 'Shipment Allocation',
        key: 'Allocation',
        width: 2,
        textAlign: 'center',
      },
      {
        text: 'Status & Required Action',
        width: 3,
        key: 'action',
        textAlign: 'left',
      },
    ];
    return arr;
  }, [isSummaryView, showUnAllocatedView]);

  const rowClicked = item => {
    navigate(`/app/scm/purchase-orders/${item._id}`);
  };

  return (
    <>
      <BodyContainer>
        <div className="sk:flex! sk:justify-end! sk:items-center! sk:w-full">
          <div className="sk:ml-2.5 sk:flex sk:flex-wrap sk:items-center sk:gap-y-1">
            {!!filters.length && <MuteText>Filters</MuteText>}
            {filters?.map(filter => (
              <FilterPill
                key={filter.name}
                data={filter}
                userValue={userValue}
                removeFilter={removeFilter}
                openRelevantFilter={openRelevantFilter}
              />
            ))}
          </div>
        </div>

        <AppTable
          RowComponent={
            // @ts-expect-error - TS2741: Property 'item' is missing in type '{ showUnAllocatedView: any; rowClicked: (ite...
            <Row
              showUnAllocatedView={showUnAllocatedView}
              rowClicked={rowClicked}
            />
          }
          tableKey="PO-table"
          loading={loading}
          data={data || []}
          currentPage={currentPage}
          pageChanged={pageChanged}
          totalPages={totalPages}
          totalData={totalData}
          // @ts-expect-error - TS2322: Type '({ text: string; width: number; key: string; textAlign: string; sortKey?: ...
          columns={columns}
          pageSize={pageSize}
          pageSizeChanged={sizeChanged}
          sort={sort}
          sortChanged={orderChanged}
        />
      </BodyContainer>
    </>
  );
}
